import { ia, isPrimitiveValue, mString, singularize, snakeToTitleCase } from 'lib/helpers/utility';

const AMOUNT_KEYS = ['amount', 'cents']; // Fields containing these substrings will be formatted as amounts
const AMOUNT_FIELDS = ['pt_balance', 'tax', 'charge']; // Exact field names to be formatted as amounts
const NON_BASIC_PERCENT_FIELDS = ['percentage', 'local_tax_rate', 'state_tax_rate']; // Basis points fields (stored as 0-10000, displayed as 0-100%)
const BASIC_PERCENT_FIELDS = []; // Simple percentage fields (stored as 0-100, displayed with % suffix)

const isNumber = (value) => value !== '' && value !== null && !isNaN(value);

const checkIsAmount = (key, value) =>
  !key?.includes('_id') &&
  !isNaN(value) &&
  (AMOUNT_FIELDS.includes(key) || AMOUNT_KEYS.some((field) => key.includes(field)));

const checkIsPercent = (key, value, fields = []) =>
  !key?.includes('_id') && !isNaN(value) && fields.includes(key);

const handleBoolean = (val) => (val ? 'Yes' : 'No');

const handleObject = (obj, parentKey) =>
  Object.keys(obj)
    .map((key) => {
      const value = obj[key];
      if (value === undefined || value === null) return null;
      const formattedKey = formatLabel(key);
      const renderedValue = renderValue({ key: key || parentKey, value });

      return formattedKey ? `${formattedKey} ${renderedValue}` : renderedValue;
    })
    .filter(Boolean)
    .join('<br/>');

export const checkIsNestedKey = (key, obj) => {
  const objKeys = Object.keys(obj || {});

  if (objKeys.includes(key)) {
    return false;
  }

  const hasPrimaryValues = obj && Object.values(obj || {}).every(isPrimitiveValue);

  return !hasPrimaryValues && key.includes('.') && key.split('.').some((k) => isNumber(k));
};

export const renderValue = ({ key, value }) => {
  if (value === undefined || value === null) {
    return 'blank';
  }

  if (typeof value === 'object') {
    const objValue = handleObject(value);

    return objValue;
  }

  if (typeof value === 'boolean') {
    return handleBoolean(value);
  }

  const isNonBasicPercent = checkIsPercent(key, value, NON_BASIC_PERCENT_FIELDS);
  if (isNonBasicPercent) {
    return `${Number(value) / 100}%`;
  }

  const isBasicPercent = checkIsPercent(key, value, BASIC_PERCENT_FIELDS);
  if (isBasicPercent) {
    return `${value}%`;
  }

  const isAmount = checkIsAmount(key, value);
  if (isAmount) {
    return mString(value);
  }

  return value || 'blank';
};

export const getOrdinal = (num) => {
  const numValue = Number(num);

  if (isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
    return '';
  }

  const ordinals = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth',
    'twentieth'
  ];

  if (numValue < ordinals.length) {
    return ordinals[numValue];
  }

  // For numbers beyond 20, use a pattern
  const lastDigit = numValue % 10;
  const lastTwoDigits = numValue % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${numValue}th`;
  }

  const suffix = lastDigit === 1 ? 'st' : lastDigit === 2 ? 'nd' : lastDigit === 3 ? 'rd' : 'th';
  return `${numValue}${suffix}`;
};

export const formatLabel = (key) => {
  if (checkIsNestedKey(key)) {
    const keys = key.split('.');

    let prefix = '';
    const numericIndex = keys.findIndex((k) => isNumber(k));

    if (numericIndex > 0) {
      prefix = keys[numericIndex - 1];
    } else {
      // Fallback: use the non-numeric key or first key
      prefix = keys.find((k) => !isNumber(k)) || keys[0] || '';
    }

    const numIndex = keys[numericIndex];

    const ordinal = getOrdinal(numIndex);

    return `${snakeToTitleCase(ordinal)} ${snakeToTitleCase(singularize(prefix))}`;
  }

  // replace the dot with a space
  key = key.replace('.', ' ');

  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

export const isEmptyOrBlank = (data) => {
  if (data === null) return true;

  // If it's an array, check each element
  if (ia(data)) {
    return data.every((item) => isEmptyOrBlank(item));
  }

  // If it's an object, check all its values
  if (typeof data === 'object') {
    const values = Object.values(data);
    return values.every((value) => isEmptyOrBlank(value));
  }

  // Check primitive value
  const strValue = String(data).trim().toLowerCase();
  return strValue === '' || strValue === 'blank' || strValue === 'null' || strValue === 'undefined';
};
